import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MapsIcon from '../assets/images/marker-icon.svg'; // Make sure to import your Maps Icon

interface AddressData {
    id: number;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    addressType: string;
    pinCode: string;
    blockCode: string;
    addressCode: string;
    latitude: string;
    longitude: string;
    createdDate: string;
}

const UserDetails: React.FC = () => {
    const navigate = useNavigate();
    const { userId } = useParams<{ userId: string }>();
    const [addressData, setAddressData] = useState<AddressData[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!userId) {
            toast.error("User ID not found.");
            navigate('/manageuser')
        } else {
            fetchAddressData();
        }
    }, [userId, navigate]);


    const fetchAddressData = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                if (!toast.isActive("auth-error")) {
                    toast.error("No auth token found", { toastId: "auth-error" });
                }
                throw new Error("No auth token found");
            }
    
            const response = await fetch(
                `https://pamsapi.vofoxsolutions.com/api/Admin/GetAddressesByUserIdAsync?userId=${userId}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
    
            if (!response.ok) {
                if (!toast.isActive("fetch-error")) {
                    toast.error("Failed to fetch address data", { toastId: "fetch-error" });
                }
                throw new Error("Failed to fetch address data");
            }
    
            const data: AddressData[] = await response.json();
            setAddressData(data);
    
            if (data.length === 0) {
                if (!toast.isActive("no-address")) {
                    toast.info("No addresses found for this user.", { toastId: "no-address" });
                }
            }
        } catch (error) {
            if (!toast.isActive("generic-error")) {
                toast.error("An error occurred while fetching address data.", { toastId: "generic-error" });
            }
        } finally {
            setIsLoading(false);
        }
    };
    

    const handleMapClick = (latitude: string, longitude: string) => {
        // Assuming you'd like to navigate to a map page or open a map in a new window
        window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, '_blank');
    };

    return (
        <div className="h-screen overflow-hidden bg-magnolia">
            <div className="flex flex-col h-screen">
                <header className="flex items-center justify-between px-6 py-3 bg-white">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-700">User Details</h1>
                    </div>
                </header>

                <main className="flex-1 overflow-x-hidden overflow-y-auto">
                    <div className="container px-6 py-6 mx-auto">
                        <h4 className="text-base sm:text-lg text-dark font-semibold">User Address Details</h4>

                        {/* Loading Spinner */}
                        {isLoading && <p className="mt-4 text-gray-500">Loading data...</p>}

                        {/* Address Data */}
                        {addressData.length > 0 && (
                            <div className="mt-6">
                                <h4 className="text-lg font-semibold">Addresses</h4>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-4">
                                    {addressData.map((address) => (
                                        <div key={address.id} className="p-6 bg-white rounded-lg shadow-md">
                                            <p className="text-sm font-semibold text-gray-600">
                                                Address Line 1: <span className="text-lg font-bold text-blue-600">{address.addressLine1}</span>
                                            </p>
                                            <p className="text-sm font-semibold text-gray-600">
                                                Address Line 2: <span className="text-sm text-blue-600">{address.addressLine2}</span>
                                            </p>
                                            <p className="text-sm font-semibold text-gray-600">
                                                Address Line 3: <span className="text-sm text-blue-600">{address.addressLine3}</span>
                                            </p>
                                            <p className="text-sm font-semibold text-gray-600">
                                                Address Type: <span className="text-sm text-blue-600">{address.addressType}</span>
                                            </p>
                                            <p className="text-sm font-semibold text-gray-600">
                                                PAM Code: <span className="text-sm text-blue-600">{`${address.pinCode}-${address.blockCode}-${address.addressCode}`}</span>
                                            </p>
                                            <p className="text-sm font-semibold text-gray-600">
                                                Created Date: <span className="text-sm text-blue-600">{address.createdDate}</span>
                                            </p>

                                            {/* Map icon */}
                                            <div className="mt-2 place-self-end hover:bg-blue-200 rounded-md">
                                                <button onClick={() => handleMapClick(address.latitude, address.longitude)} title="View on Map" className="flex items-center text-blue-600 hover:text-blue-800">
                                                    <img src={MapsIcon} alt="Map Icon" className="w-6 h-6 mr-2" />
                                                    
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </main>

                <ToastContainer />
            </div>
        </div>
    );
};

export default UserDetails;
